import { ChevronDown, Link } from "lucide-react";
import {
  IconProtocol,
  IconClient,
  IconPlatform,
  IconSelfhost,
} from "@/app/components/icons";
import { HighlightBanner, Section, SemanticTerm, brandPalette } from "@/app/components/brand-scaffold";
import { architecturePillars, type ArchitecturePillar } from "@/design-system/registry/homepage";

const pillarProductIconMap = {
  Protocol: IconProtocol,
  Client: IconClient,
  Platform: IconPlatform,
  Selfhost: IconSelfhost,
} as const;

function PillarCard({ p }: { p: ArchitecturePillar }) {
  const ProductIcon = pillarProductIconMap[p.productIcon];
  const lightOnCard = p.text?.includes("text-white");
  const subtitleToneClass = lightOnCard
    ? "text-white"
    : p.text?.includes("text-black")
      ? "text-black"
      : "text-zinc-700";
  const bodyToneClass = lightOnCard ? "text-white" : "text-black";

  return (
    <div className={`border-4 border-black ${p.bg} ${p.hoverBg} ${p.text ?? "text-black"} cursor-pointer p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] flex flex-col transition-all duration-300 hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]`}>
      <div className="mb-6 flex-shrink-0" aria-hidden>
        <ProductIcon className="h-12 w-12 border-4 border-black" />
      </div>
      <h3 className={`text-2xl font-black uppercase mb-2 ${lightOnCard ? "text-white" : "text-black"}`}>{p.title}</h3>
      <p className={`text-xs font-black uppercase tracking-wider mb-4 opacity-80 ${subtitleToneClass}`}>{p.subtitle}</p>
      <p className={`text-sm font-bold opacity-90 leading-relaxed ${bodyToneClass}`}>{p.desc}</p>
    </div>
  );
}

/**
 * S11 · FOR ENGINEERS — Architecture
 *
 * Per OPC narrative spec §11: keep the entire technical pillar + topology
 * disclosure but fold it behind a brutalist <details> so the architecture
 * doesn't dominate the page above-the-fold. Eyebrow shifts from
 * "ARCHITECTURE" to "FOR ENGINEERS · 协议是这样运转的"; description is
 * trimmed; the 4 pillars + network topology stay byte-for-byte intact.
 */
export function HomepageArchitecture() {
  return (
    <Section
      id="architecture"
      eyebrow="FOR ENGINEERS"
      title="协议是这样运转的"
      description="如果你想知道一根 Hotline 在工程层面如何被发起、被路由、被结算，下面这块可以展开。"
    >
      <details className="group border-4 border-black bg-white/85 backdrop-blur-sm shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
        <summary className="flex items-center justify-between gap-4 cursor-pointer select-none px-5 py-4 sm:px-6 sm:py-5 list-none [&::-webkit-details-marker]:hidden border-b-4 border-black bg-[#A3E635] hover:bg-[#A3E635]/85 transition-colors">
          <div className="flex items-center gap-3">
            <span className="border-2 border-black bg-black px-2 py-1 text-[10px] font-black uppercase tracking-widest text-[#A3E635]">
              FOR ENGINEERS
            </span>
            <span className="text-sm sm:text-base font-black uppercase text-black tracking-tight">
              展开协议详情
            </span>
          </div>
          <ChevronDown
            className="h-5 w-5 text-black transition-transform duration-200 group-open:rotate-180"
            strokeWidth={3}
          />
        </summary>

        <div className="p-5 sm:p-6 lg:p-8 space-y-12">
        {/* 4 Pillars */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {architecturePillars.map((p) => <PillarCard key={p.title} p={p} />)}
        </div>

        {/* Network Topology — simplified: Platform 居顶 → 两条 HTTPS 下到 Caller/Responder → 数据面横向直连 */}
        <div className="border-4 border-black bg-white p-6 md:p-10 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
          <h3 className="text-xl font-black uppercase mb-8 border-b-4 border-black pb-4 inline-block">网络拓扑</h3>

          <div className="bg-zinc-50 border-4 border-black p-4 md:p-6 relative">
            {/* 整张拓扑 — 改为单一内联 SVG，方向、对齐都精确可控；
                  关键：TransportAdapter chip 真正夹在上下两条数据虚线之间，作为"数据面主轴"标签 */}
            <svg
              viewBox="0 0 1100 700"
              role="img"
              aria-label="Call Anything 网络拓扑：Platform 控制面在顶，两个 Client 进程组分别承载 Caller / Responder Controller，控制面走 HTTPS 至 Platform，业务数据走 TransportAdapter（local · relay_http · email）在两个 Controller 间直连。"
              className="w-full h-auto block"
              style={{ background: '#FAFAFA', fontFamily: 'Inter, "Helvetica Neue", Helvetica, Arial, sans-serif' }}
            >
              {/* Protocol 水印 — eyebrow 级 tiny label */}
              <text x="40" y="58" fontSize="20" fontWeight="900" fill="#9CA3AF" letterSpacing="2">PROTOCOL: @delexec/contracts</text>
              <text x="40" y="78" fontSize="12" fontWeight="900" fill="#9CA3AF" letterSpacing="1.5">THE SINGLE SOURCE OF TRUTH</text>

              {/* PLATFORM — text-2xl 主标题 + text-xs 副标 + text-xs mono 字段串 */}
              <rect x="358" y="88" width="384" height="120" fill="#111111" />
              <rect x="350" y="80" width="384" height="120" fill="#8B5CF6" stroke="#111111" strokeWidth="6" />
              <text x="542" y="125" fontSize="28" fontWeight="900" fill="#FFFFFF" textAnchor="middle" letterSpacing="1">PLATFORM</text>
              <text x="542" y="151" fontSize="12" fontWeight="900" fill="#F3F4F6" textAnchor="middle" letterSpacing="2">控制面服务</text>
              <text x="542" y="180" fontSize="12" fontWeight="900" fill="#FFFFFF" textAnchor="middle" fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace">catalog · token · delivery-meta · metrics</text>

              {/* SELFHOST 角标 — 双行：text-sm 身份 + text-[10px] 副标 */}
              <rect x="668" y="58" width="160" height="58" fill="#111111" />
              <rect x="664" y="54" width="160" height="58" fill="#FACC15" stroke="#111111" strokeWidth="4" />
              <text x="744" y="79" fontSize="14" fontWeight="900" fill="#111111" textAnchor="middle" letterSpacing="1">或 SELFHOST</text>
              <text x="744" y="98" fontSize="10" fontWeight="900" fill="#111111" textAnchor="middle" letterSpacing="0.8">同组件 · 两种部署</text>

              {/* CONTROL PLANE: HTTPS — Caller-Client → Platform */}
              <path d="M 260 350 L 260 260 L 450 260 L 450 215" fill="none" stroke="#111111" strokeWidth="6" />
              <polygon points="450,200 440,220 460,220" fill="#111111" />
              <rect x="275" y="245" width="160" height="30" fill="#FFFFFF" stroke="#111111" strokeWidth="4" />
              <text x="355" y="264" fontSize="12" fontWeight="900" fill="#111111" textAnchor="middle" letterSpacing="0.8">HTTPS · 请求/响应</text>

              {/* CONTROL PLANE: HTTPS — Responder-Client → Platform */}
              <path d="M 840 350 L 840 260 L 634 260 L 634 215" fill="none" stroke="#111111" strokeWidth="6" />
              <polygon points="634,200 624,220 644,220" fill="#111111" />
              <rect x="657" y="245" width="160" height="30" fill="#FFFFFF" stroke="#111111" strokeWidth="4" />
              <text x="737" y="264" fontSize="12" fontWeight="900" fill="#111111" textAnchor="middle" letterSpacing="0.8">HTTPS · 请求/响应</text>

              {/* CLIENT 1 — Caller-side 进程组 */}
              <rect x="88" y="358" width="360" height="320" fill="#111111" />
              <rect x="80" y="350" width="360" height="320" fill="#FFFFFF" stroke="#3B82F6" strokeWidth="6" strokeDasharray="12 12" />
              <rect x="80" y="350" width="360" height="50" fill="#3B82F6" stroke="#111111" strokeWidth="6" />
              <text x="260" y="382" fontSize="16" fontWeight="900" fill="#FFFFFF" textAnchor="middle" letterSpacing="0.8">CLIENT (本地进程组)</text>

              <rect x="128" y="478" width="280" height="100" fill="#111111" />
              <rect x="120" y="470" width="280" height="100" fill="#14B8A6" stroke="#111111" strokeWidth="6" />
              <text x="260" y="513" fontSize="20" fontWeight="900" fill="#111111" textAnchor="middle" letterSpacing="0.5">CALLER</text>
              <text x="260" y="543" fontSize="20" fontWeight="900" fill="#111111" textAnchor="middle" letterSpacing="0.5">CONTROLLER</text>

              {/* CLIENT 2 — Responder-side 进程组 */}
              <rect x="668" y="358" width="360" height="320" fill="#111111" />
              <rect x="660" y="350" width="360" height="320" fill="#FFFFFF" stroke="#3B82F6" strokeWidth="6" strokeDasharray="12 12" />
              <rect x="660" y="350" width="360" height="50" fill="#3B82F6" stroke="#111111" strokeWidth="6" />
              <text x="840" y="382" fontSize="16" fontWeight="900" fill="#FFFFFF" textAnchor="middle" letterSpacing="0.8">CLIENT (本地进程组)</text>

              <rect x="688" y="478" width="280" height="100" fill="#111111" />
              <rect x="680" y="470" width="280" height="100" fill="#F97316" stroke="#111111" strokeWidth="6" />
              <text x="820" y="513" fontSize="20" fontWeight="900" fill="#111111" textAnchor="middle" letterSpacing="0.5">RESPONDER</text>
              <text x="820" y="543" fontSize="20" fontWeight="900" fill="#111111" textAnchor="middle" letterSpacing="0.5">CONTROLLER</text>

              {/* DATA PLANE — 上行 TaskContract (Caller → Responder) */}
              <text x="540" y="463" fontSize="14" fontWeight="900" fill={brandPalette.green} textAnchor="middle" letterSpacing="0.5">TaskContract</text>
              <path d="M 400 478 L 655 478" fill="none" stroke={brandPalette.green} strokeWidth="6" strokeDasharray="14 10" />
              <polygon points="675,478 650,468 650,488" fill={brandPalette.green} />

              {/* TRANSPORT ADAPTER chip — 严格夹在两条数据虚线之间；center x=540 与上下数据线标签共轴 */}
              <rect x="428" y="498" width="232" height="50" fill="#111111" />
              <rect x="424" y="494" width="232" height="50" fill="#A3E635" stroke="#111111" strokeWidth="4" />
              <text x="540" y="515" fontSize="12" fontWeight="900" fill="#111111" textAnchor="middle" letterSpacing="0.8">TRANSPORT ADAPTER</text>
              <text x="540" y="535" fontSize="12" fontWeight="900" fill="#111111" textAnchor="middle" fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace">local · relay_http · email</text>

              {/* DATA PLANE — 下行 ResultPackage (Responder → Caller) */}
              <path d="M 680 562 L 425 562" fill="none" stroke={brandPalette.green} strokeWidth="6" strokeDasharray="14 10" />
              <polygon points="405,562 430,552 430,572" fill={brandPalette.green} />
              <text x="540" y="585" fontSize="14" fontWeight="900" fill={brandPalette.green} textAnchor="middle" letterSpacing="0.5">ResultPackage</text>
            </svg>

            {/* Client 角色 footnote — 解决"Client 是什么 / 部署灵活性"的关键说明 */}
            <p className="text-center mt-4 pt-4 border-t-2 border-dashed border-zinc-300 text-[11px] font-bold text-zinc-600 leading-relaxed max-w-3xl mx-auto">
              <span className="font-black text-zinc-900">Client = 用户本地进程组</span>。图中按"分机部署"展示便于讲清双向通信；
              实际上一个 Client 也可以<span className="font-black text-zinc-900">同时承载 Caller + Responder 两个 Controller</span>（同机部署，由 Ops Supervisor 编排）。
            </p>
          </div>

          {/* Topology Legend — 3 条改为：控制面 / 数据面 / 部署形态。Mini 箭头与上方主图保持一致（CSS 三角） */}
          <div className="mt-8 grid gap-4 lg:grid-cols-3 border-t-4 border-black pt-6">
            <div className="flex items-start gap-3">
              <div className="w-10 flex-shrink-0 flex items-center justify-center h-6 relative">
                {/* 一条短横线 + 右端朝右黑色小三角，象征"Client → Platform 调用" */}
                <div className="w-7 bg-black" style={{ height: 2 }} />
                <div
                  className="absolute right-0 top-1/2 -translate-y-1/2"
                  style={{
                    width: 0, height: 0,
                    borderTop: '5px solid transparent',
                    borderBottom: '5px solid transparent',
                    borderLeft: '7px solid #111',
                  }}
                />
              </div>
              <div>
                <h4 className="font-black text-sm uppercase">控制面 · HTTPS</h4>
                <p className="text-xs font-bold text-zinc-500 mt-1"><SemanticTerm tone="client">Client</SemanticTerm> 主动调 <SemanticTerm tone="platform">Platform</SemanticTerm> / <SemanticTerm tone="selfhost">Selfhost</SemanticTerm><br/>catalog · token · delivery-meta · metrics</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 flex-shrink-0 flex flex-col items-center justify-center h-6 gap-1 relative">
                {/* 上：朝右的绿色虚线 + 右端三角 */}
                <div className="relative w-full">
                  <div className="w-7 mx-auto" style={{ height: 0, borderTop: `2px dashed ${brandPalette.green}` }} />
                  <div
                    className="absolute top-1/2 -translate-y-1/2"
                    style={{
                      right: 'calc(50% - 14px)',
                      width: 0, height: 0,
                      borderTop: '4px solid transparent',
                      borderBottom: '4px solid transparent',
                      borderLeft: `6px solid ${brandPalette.green}`,
                    }}
                  />
                </div>
                {/* 下：朝左的绿色虚线 + 左端三角 */}
                <div className="relative w-full">
                  <div className="w-7 mx-auto" style={{ height: 0, borderTop: `2px dashed ${brandPalette.green}` }} />
                  <div
                    className="absolute top-1/2 -translate-y-1/2"
                    style={{
                      left: 'calc(50% - 14px)',
                      width: 0, height: 0,
                      borderTop: '4px solid transparent',
                      borderBottom: '4px solid transparent',
                      borderRight: `6px solid ${brandPalette.green}`,
                    }}
                  />
                </div>
              </div>
              <div>
                <h4 className="font-black text-sm uppercase">数据面 · TransportAdapter</h4>
                <p className="text-xs font-bold text-zinc-500 mt-1">TaskContract <SemanticTerm tone="caller">Caller Ctrl</SemanticTerm> → <SemanticTerm tone="responder">Responder Ctrl</SemanticTerm><br/>ResultPackage <SemanticTerm tone="responder">Responder Ctrl</SemanticTerm> → <SemanticTerm tone="caller">Caller Ctrl</SemanticTerm></p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 flex-shrink-0 flex items-center justify-center h-6"><Link className="h-5 w-5" /></div>
              <div>
                <h4 className="font-black text-sm uppercase">部署形态</h4>
                <p className="text-xs font-bold text-zinc-500 mt-1"><SemanticTerm tone="platform">Platform</SemanticTerm> 与 <SemanticTerm tone="selfhost">Selfhost</SemanticTerm> 二选一 · 能力对等<br/><SemanticTerm tone="client">Client</SemanticTerm> 可只跑一边、也可同机两边</p>
              </div>
            </div>
          </div>
        </div>

        {/* 工作机制 Banner — 文案更新：强调控制面/数据面分离 + Platform 不 hop 业务流量 */}
        <HighlightBanner badgeText="工作机制" shadowClass="shadow-[2px_2px_0px_0px_rgba(163,230,53,1)]" badgeColor="bg-black" className="gap-6 mt-0">
          <p className="text-sm font-bold text-zinc-900 leading-relaxed">
            <span className="inline-block align-middle border border-black bg-[#A3E635] px-1.5 py-0.5 text-xs font-black text-black mr-1">PROTOCOL</span> 是真相源；
            <span className="inline-block align-middle border border-black bg-[#3B82F6] px-1.5 py-0.5 text-xs font-black text-white mr-1">CLIENT</span>（用户本地）跑 <SemanticTerm tone="caller">Caller</SemanticTerm> / <SemanticTerm tone="responder">Responder</SemanticTerm> Controller，是协议的实施者；
            <span className="inline-block align-middle border border-black bg-[#8B5CF6] px-1.5 py-0.5 text-xs font-black text-white mr-1">PLATFORM</span>
            {" 与 "}
            <span className="inline-block align-middle border border-black bg-[#FACC15] px-1.5 py-0.5 text-xs font-black text-black mr-1">SELFHOST</span>
            是同一份控制面服务的两种部署形态。控制面走 HTTPS（catalog · token · delivery-meta · metrics）；业务流量走 <span className="font-mono font-black text-black">TransportAdapter</span>（local · relay_http · email）—— <span className="underline decoration-2 underline-offset-2">Platform 不在数据面 hop</span>。
          </p>
        </HighlightBanner>
        </div>
      </details>
    </Section>
  );
}
